import React, { useEffect, useState, useMemo } from "react";

/**
 * Fase 11.5 Sprint 1 — Item 3: Countdown a próximo corte de procesamiento.
 *
 * Lógica:
 * - Horario operativo: Lun-Vie 9:00 a 18:00 COT (America/Bogota, UTC-5).
 * - Si pagas antes de las 18:00 de un día hábil → tu caso entra en cola hoy.
 *   Entrega esperada: ~48h hábiles después.
 * - Si pagas después de las 18:00 o fin de semana → entra en cola al siguiente día hábil.
 *
 * Props:
 *   level: "nivel1" | "nivel2"  // afecta el SLA (48h N1 vs 72h N2)
 *   testid: opcional
 */

const CUTOFF_HOUR_COT = 18; // 6 PM hora Bogotá
const START_HOUR_COT = 9;
// Bogotá es UTC-5 sin DST
const BOGOTA_UTC_OFFSET_HOURS = -5;

function nowInBogota() {
  // Devuelve un Date que representa "ahora en Bogotá".
  // Trick: ajusta del UTC actual al offset de Bogotá.
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  return new Date(utcMs + BOGOTA_UTC_OFFSET_HOURS * 3600_000);
}

function isWeekend(d) {
  const day = d.getDay(); // 0=Dom, 6=Sab
  return day === 0 || day === 6;
}

function nextBusinessDayAt(d, hour) {
  // Avanza al siguiente día hábil con la hora indicada.
  const next = new Date(d.getTime());
  next.setDate(next.getDate() + 1);
  next.setHours(hour, 0, 0, 0);
  while (isWeekend(next)) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

function addBusinessHours(start, hoursToAdd) {
  // Suma "horas hábiles" considerando solo 9-18 lun-vie.
  // Aproximación: cada día hábil aporta 9h hábiles.
  let cursor = new Date(start.getTime());
  let hoursLeft = hoursToAdd;
  // Si cursor está fuera del horario hábil, avanza al inicio del próximo día hábil.
  if (isWeekend(cursor) || cursor.getHours() >= CUTOFF_HOUR_COT) {
    cursor = nextBusinessDayAt(cursor, START_HOUR_COT);
  } else if (cursor.getHours() < START_HOUR_COT) {
    cursor.setHours(START_HOUR_COT, 0, 0, 0);
  }
  while (hoursLeft > 0) {
    const hoursLeftInDay = CUTOFF_HOUR_COT - cursor.getHours() - cursor.getMinutes() / 60;
    if (hoursLeft <= hoursLeftInDay) {
      cursor = new Date(cursor.getTime() + hoursLeft * 3600_000);
      hoursLeft = 0;
    } else {
      hoursLeft -= hoursLeftInDay;
      cursor = nextBusinessDayAt(cursor, START_HOUR_COT);
    }
  }
  return cursor;
}

function formatHumanDate(d) {
  // Ej: "lunes 26 de mayo, 5:00 PM"
  const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  const days = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
  const dn = days[d.getDay()];
  const day = d.getDate();
  const month = months[d.getMonth()];
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dn} ${day} de ${month}, ${h}:${min} ${ampm}`;
}

function formatHHMMSS(ms) {
  if (ms <= 0) return "00:00:00";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function PaymentCountdown({ level = "nivel1", testid }) {
  const slaHours = level === "nivel2" ? 72 : 48;
  const levelLabel = level === "nivel2" ? "Kit Documental" : "Diagnóstico";

  // Calcula próximo corte (18:00 del siguiente día hábil con horario)
  const cutoffTarget = useMemo(() => {
    const nb = nowInBogota();
    const sameDayCutoff = new Date(nb.getTime());
    sameDayCutoff.setHours(CUTOFF_HOUR_COT, 0, 0, 0);
    if (isWeekend(nb) || nb.getHours() >= CUTOFF_HOUR_COT) {
      // El próximo corte es el siguiente día hábil a las 18:00
      return nextBusinessDayAt(nb, CUTOFF_HOUR_COT);
    }
    return sameDayCutoff;
  }, []);

  // Entrega esperada si paga antes del corte de hoy
  const deliveryIfPayNow = useMemo(() => {
    return addBusinessHours(nowInBogota(), slaHours);
  }, [slaHours]);

  const [remainingMs, setRemainingMs] = useState(() =>
    cutoffTarget.getTime() - nowInBogota().getTime()
  );

  useEffect(() => {
    const id = setInterval(() => {
      setRemainingMs(cutoffTarget.getTime() - nowInBogota().getTime());
    }, 1000);
    return () => clearInterval(id);
  }, [cutoffTarget]);

  const afterCutoff = remainingMs <= 0;
  const nb = nowInBogota();
  const isOutsideHours = isWeekend(nb) || nb.getHours() < START_HOUR_COT || nb.getHours() >= CUTOFF_HOUR_COT;

  return (
    <div
      className="border border-amber-200 bg-amber-50/40 p-5 mb-6"
      data-testid={testid || "payment-countdown"}
    >
      <p className="text-[11px] uppercase tracking-widest text-amber-800 font-medium mb-2">
        Tiempo estimado de entrega
      </p>
      {!isOutsideHours && !afterCutoff && (
        <>
          <p className="text-sm text-gray-800 mb-3">
            Procesamos confirmaciones de pago en horario hábil (Lun–Vie, 9:00 a 18:00 COT).
            <strong> Si pagas antes de las 6:00 PM de hoy</strong>, tu {levelLabel} entrará
            en cola y estará entregado aproximadamente en:
          </p>
          <div className="bg-white border border-amber-300 px-4 py-3 inline-block rounded-sm mb-3">
            <p className="font-serif text-2xl text-gray-900" data-testid="countdown-clock">
              {formatHHMMSS(remainingMs)}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-amber-700 mt-1">
              Tiempo restante hasta el cierre de hoy
            </p>
          </div>
          <p className="text-sm text-gray-700">
            Entrega estimada: <strong data-testid="countdown-delivery-date">
              {formatHumanDate(deliveryIfPayNow)}
            </strong>
          </p>
        </>
      )}
      {(isOutsideHours || afterCutoff) && (
        <p className="text-sm text-gray-800">
          Estamos fuera de horario hábil (Lun–Vie, 9:00 a 18:00 COT). Tu pago entrará en cola
          al inicio del siguiente día hábil. <br />
          Entrega estimada: <strong data-testid="countdown-delivery-date">
            {formatHumanDate(deliveryIfPayNow)}
          </strong>
        </p>
      )}
      <p className="text-[11px] text-gray-500 mt-3">
        SLA basado en {slaHours}h hábiles. Te avisaremos por email apenas confirmemos tu pago.
      </p>
    </div>
  );
}
